/**
 * Activity's nav badge — `docs/activity.md` §13.2 (Board 1): one number,
 * unread conversations plus tasks due today or late, for the people this
 * device last showed; green (`tone: 'accent'`), and nothing at all at zero.
 *
 * Asked once on load, again on every `activity.changed` on the events stream
 * and every 60 s as a fallback. While the Activity page is open it already
 * holds the same answer for its tab counts, and says so on
 * `ACTIVITY_BADGE_EVENT`, so the two never disagree.
 */
import { useEffect, useRef, useState } from 'react'
import type { ActivityState } from '@rowted/shared'
import type { NavBadge } from '../frame/nav.js'
import { activityRequest, peopleQuery } from './api.js'

export const ACTIVITY_BADGE_EVENT = 'rowted:activity-badge'
/** The events stream said something changed: whatever counts, asks again. */
export const ACTIVITY_CHANGED_EVENT = 'rowted:activity-changed'
export interface ActivityBadgeDetail { unread: number; dueOrLate: number }

export function announceBadge(detail: ActivityBadgeDetail): void {
  window.dispatchEvent(new CustomEvent<ActivityBadgeDetail>(ACTIVITY_BADGE_EVENT, { detail }))
}

const badgeOf = (detail: ActivityBadgeDetail | null): NavBadge | undefined => {
  const count = detail ? detail.unread + detail.dueOrLate : 0
  return count > 0 ? { text: String(count), tone: 'accent' } : undefined
}

export function useActivityNavBadge(enabled: boolean): NavBadge | undefined {
  const [detail, setDetail] = useState<ActivityBadgeDetail | null>(null)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled) return undefined
    let live = true
    const ask = async () => {
      try {
        const { state } = await activityRequest<{ state: ActivityState }>('/activity/state')
        const answer = await activityRequest<ActivityBadgeDetail>(
          `/activity/badge?people=${peopleQuery(state.people.ids)}&unassigned=${peopleQuery(state.people.unassigned)}`)
        if (live) setDetail(answer)
      } catch {
        /* The badge is a nicety: a failed ask keeps the last number. */
      }
    }
    const soon = () => {
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => void ask(), 400)
      /* The page's tab counts ask again on the same nudge. */
      window.dispatchEvent(new Event(ACTIVITY_CHANGED_EVENT))
    }
    void ask()
    const every = window.setInterval(() => void ask(), 60_000)
    const onPage = (event: Event) => setDetail((event as CustomEvent<ActivityBadgeDetail>).detail)
    window.addEventListener(ACTIVITY_BADGE_EVENT, onPage)
    let stream: EventSource | null = null
    if (typeof EventSource !== 'undefined') {
      stream = new EventSource('/api/v1/events')
      stream.addEventListener('activity.changed', soon)
    }
    return () => {
      live = false
      window.clearInterval(every)
      if (timer.current) window.clearTimeout(timer.current)
      window.removeEventListener(ACTIVITY_BADGE_EVENT, onPage)
      stream?.close()
    }
  }, [enabled])

  return badgeOf(detail)
}
